import BodyText from "components/Typography/BodyText"
import Heading from "components/Typography/Heading"
import { memo, useCallback, useMemo, useState } from "react"
import styled from "styled-components"
import ListTypeDisc from "../ListComponent/ListTypeDisc"
import { Collapsible } from "../Transition/Transition"
import IconArrowUp from "components/EvtKit/icons/IconArrowUp"
import IconArrowDown from "components/EvtKit/icons/IconArrowDown"
import IconProducts from "../icons/IconProducts"
import IconEventTypes from "../icons/IconEventTypes"
import IconPurchasePlans from "../icons/IconPurchasePlans"
import IconAccounts from "../icons/IconAccounts"
import IconInvoices from "../icons/IconInvoices"
import IconApiEndpoints from "../icons/IconApiEndpoints"
import IconWebhooks from "../icons/IconWebhooks"
import { AppState, useAppSelector } from "reducers/store"
import Spinner from "components/EvtKit/spinner/Spinner"
import { useQueryState } from "context/queryState"
import { FIRST_STEPS } from "reducers/accounts/actions-types"
import ApiAccounts from "api/ApiAccounts"
import { useDebounce } from "use-debounce"
import { firstStepsTotal } from "constants/firstSteps"
import clsx from "clsx"
import { IconCheckGreen } from "../icons/FirstSteps/IconCheck"
import IconRocket from "../icons/FirstSteps/IconRocket"
import FirstSteps from "../FirstSteps/FirstSteps"

const ContainerList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ContainerFaq = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  background: ${(props) => props.theme.palette.white};
`

const Introduction = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  flex-direction: column;
  padding: 16px 24px;
  box-shadow: 0px 1px 8px 0px #7b61ff;
  border-radius: 8px;

  &.firstSteps {
    box-shadow: 0px 1px 4px 0px ${(props) => props.theme.palette.container_shadow};
  }
`

const IntroductionTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`

const IntroductionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ContainerArrow = styled.div`
  width: 36px;
  height: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
`

const TitleIcon = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  flex-direction: row;

  &.completed {
    gap: 16px;
  }
`

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 8px;
  gap: 16px;
  width: 100%;
`

const FirstStepsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`

const FirstStepsTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

export interface contentSections {
  products: boolean
  eventTypes: boolean
  purchasePlans: boolean
  clients: boolean
  invoices: boolean
  apiEndpoints: boolean
  webhooks: boolean
}

const ListContent = () => {
  const [isOpen, setIsOpen] = useState<contentSections>({
    products: true,
    eventTypes: false,
    purchasePlans: false,
    clients: false,
    invoices: false,
    apiEndpoints: false,
    webhooks: false
  })

  useQueryState([FIRST_STEPS], async () => await new ApiAccounts().getFirstSteps(), FIRST_STEPS, {
    refetchOnMount: "always"
  })

  const { firstStepsStatus, loading } = useAppSelector((x: AppState) => x.accounts.firstSteps)

  const [debouncedLoading] = useDebounce(loading, 500)

  const completedSteps = useMemo(() => {
    const count = Object.values(firstStepsStatus).filter(Boolean).length

    const isCompleted = count === firstStepsTotal

    return {
      count,
      isCompleted
    }
  }, [firstStepsStatus])

  const isCompleted = completedSteps.isCompleted

  const handleClickSection = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const key = e.currentTarget.id

    setIsOpen((prevState) => ({
      ...prevState,
      [key]: !prevState[key]
    }))
  }, [])

  return (
    <ContainerList>
      {debouncedLoading && <Spinner className="primary-dark size-medium" />}
      {!debouncedLoading && (
        <ContainerFaq>
          <Introduction className="firstSteps">
            <FirstStepsTitle>
              <TitleIcon className={clsx({ completed: isCompleted })}>
                {isCompleted ? <IconCheckGreen /> : <IconRocket />}
                <Heading variant="h5" color={isCompleted ? "success" : "primary_alt_medium"}>
                  {isCompleted ? "Parabéns! Você concluiu os primeiros passos." : "Primeiros passos"}
                </Heading>
              </TitleIcon>
            </FirstStepsTitle>
            <InnerContainer>
              {isCompleted && (
                <BodyText size="medium" color="gray-dark">
                  Agora é só aproveitar a nossa plataforma!
                </BodyText>
              )}
              <FirstStepsContainer>
                <FirstSteps
                  setIsOpen={setIsOpen}
                  firstStepsStatus={firstStepsStatus}
                  completedStepsCount={completedSteps.count}
                />
              </FirstStepsContainer>
            </InnerContainer>
          </Introduction>
        </ContainerFaq>
      )}
      <ContainerFaq>
        <Introduction>
          <IntroductionTitle id="products" onClick={handleClickSection}>
            <TitleIcon>
              <IconProducts />
              <Heading variant="h5" color="primary_alt_medium">
                Produtos
              </Heading>
            </TitleIcon>
            <ContainerArrow>{isOpen.products ? <IconArrowUp /> : <IconArrowDown />}</ContainerArrow>
          </IntroductionTitle>
          <Collapsible open={isOpen.products}>
            <IntroductionContent>
              <ListTypeDisc
                title="Produto externo:"
                description="O Shelf é responsável pela cobrança, gestão de acesso e chaves API, além de interagir diretamente com a sua API."
                className="disc"
                classNameLI="list-item"
              />
              <ListTypeDisc
                title="Produto federado:"
                description="Produtos ou serviços que envolvem um portal externo, onde se deseja controlar funcionalidades através do Shelf, utilizando-o como SSO e controle de cobrança."
                className="disc"
                classNameLI="list-item"
              />
            </IntroductionContent>
          </Collapsible>
        </Introduction>
      </ContainerFaq>
      <ContainerFaq>
        <Introduction>
          <IntroductionTitle id="eventTypes" onClick={handleClickSection}>
            <TitleIcon>
              <IconEventTypes />
              <Heading variant="h5" color="primary_alt_medium">
                Tipos de Eventos
              </Heading>
            </TitleIcon>
            <ContainerArrow>{isOpen.eventTypes ? <IconArrowUp /> : <IconArrowDown />}</ContainerArrow>
          </IntroductionTitle>
          <Collapsible open={isOpen.eventTypes}>
            <IntroductionContent>
              <BodyText size="medium" weight="regular" color="gray-dark">
                É o parâmetro ou variável utilizado para determinar a forma de cobrança do produto. Por exemplo, no caso
                de cobrança por minuto, você definirá que a cobrança será baseada no consumo de minutos.
              </BodyText>
            </IntroductionContent>
          </Collapsible>
        </Introduction>
      </ContainerFaq>
      <ContainerFaq>
        <Introduction>
          <IntroductionTitle id="purchasePlans" onClick={handleClickSection}>
            <TitleIcon>
              <IconPurchasePlans />
              <Heading variant="h5" color="primary_alt_medium">
                Planos
              </Heading>
            </TitleIcon>
            <ContainerArrow>{isOpen.purchasePlans ? <IconArrowUp /> : <IconArrowDown />}</ContainerArrow>
          </IntroductionTitle>
          <Collapsible open={isOpen.purchasePlans}>
            <IntroductionContent>
              <ListTypeDisc
                title="Precificação simplificada:"
                description="Você escolhe os tipos de eventos relacionados ao plano, que são cobrados conforme o uso, quando essa opção é selecionada. Isso significa que o valor final é baseado no consumo."
                className="disc"
                classNameLI="list-item"
              />
              <ListTypeDisc
                title="Precificação no modo desenvolvedor:"
                description="O valor final do plano é calculado com base em uma expressão matemática que utiliza os tipos de eventos como variáveis. Essa relação pode ser determinada pela quantidade de eventos utilizados, pelo preço dos eventos ou por um valor pré-definido, selecionado no checkout do plano."
                className="disc"
                classNameLI="list-item"
              />
            </IntroductionContent>
          </Collapsible>
        </Introduction>
      </ContainerFaq>
      <ContainerFaq>
        <Introduction>
          <IntroductionTitle id="clients" onClick={handleClickSection}>
            <TitleIcon>
              <IconAccounts />
              <Heading variant="h5" color="primary_alt_medium">
                Contas
              </Heading>
            </TitleIcon>
            <ContainerArrow>{isOpen.clients ? <IconArrowUp /> : <IconArrowDown />}</ContainerArrow>
          </IntroductionTitle>
          <Collapsible open={isOpen.clients}>
            <IntroductionContent>
              <BodyText size="medium" weight="regular" color="gray-dark">
                Você pode visualizar as contas vinculadas aos seus produtos, editar uma conta para torná-la uma conta
                ‘enterprise ou remover. Além disso, é possível adicionar um bônus de uso a uma conta sem custo extra.
              </BodyText>
              <ListTypeDisc
                title="Conta padrão:"
                description="Possui um limite de valor de cobrança progressivo. Quando esse limite é atingido, ocorre a cobrança, e se bem-sucedida, o limite aumenta. Esses planos são considerados 'padrões', acessíveis a qualquer conta."
                className="disc"
                classNameLI="list-item"
              />
              <ListTypeDisc
                title="Contas enterprise:"
                description="Permite a compra de planos do tipo 'enterprise'. Nesses casos, a cobrança ocorre uma vez por mês, dependendo do consumo realizado, sem um limite de valor específico."
                className="disc"
                classNameLI="list-item"
              />
            </IntroductionContent>
          </Collapsible>
        </Introduction>
      </ContainerFaq>
      <ContainerFaq>
        <Introduction>
          <IntroductionTitle id="invoices" onClick={handleClickSection}>
            <TitleIcon>
              <IconInvoices />
              <Heading variant="h5" color="primary_alt_medium">
                Faturas
              </Heading>
            </TitleIcon>
            <ContainerArrow>{isOpen.invoices ? <IconArrowUp /> : <IconArrowDown />}</ContainerArrow>
          </IntroductionTitle>
          <Collapsible open={isOpen.invoices}>
            <IntroductionContent>
              <ListTypeDisc
                description="Você pode visualizar as faturas (invoices) dos seus produtos, além do status de cada conta vinculada: pago, pendente ou pagamento recusado. Em geral, uma fatura é um recibo sem valor fiscal."
                className="disc"
                classNameLI="list-item"
              />
            </IntroductionContent>
          </Collapsible>
        </Introduction>
      </ContainerFaq>
      <ContainerFaq>
        <Introduction>
          <IntroductionTitle id="apiEndpoints" onClick={handleClickSection}>
            <TitleIcon>
              <IconApiEndpoints />
              <Heading variant="h5" color="primary_alt_medium">
                API Endpoints
              </Heading>
            </TitleIcon>
            <ContainerArrow>{isOpen.apiEndpoints ? <IconArrowUp /> : <IconArrowDown />}</ContainerArrow>
          </IntroductionTitle>
          <Collapsible open={isOpen.apiEndpoints}>
            <IntroductionContent>
              <BodyText size="medium" weight="regular" color="gray-dark">
                Os endpoints permitem definir rotas personalizadas para acessar recursos do seu produto por meio de
                requisições HTTP. Ao criar um endpoint, você pode configurar caminhos internos e públicos, definir se
                existem parâmetros na URL, escolher o método HTTP adequado (GET, POST, PUT, DELETE, PATCH ou OPTIONS),
                além de documentar detalhadamente o comportamento esperado para cada rota.
              </BodyText>
              <ListTypeDisc
                title="Request Parameters:"
                description="Defina os parâmetros esperados nas requisições enviadas ao seu endpoint, facilitando a integração externa."
                className="disc"
                classNameLI="list-item"
              />
              <ListTypeDisc
                title="Response Parameters:"
                description="Especifique a estrutura dos dados retornados, garantindo clareza e facilidade de consumo por outras aplicações."
                className="disc"
                classNameLI="list-item"
              />
            </IntroductionContent>
          </Collapsible>
        </Introduction>
      </ContainerFaq>
      <ContainerFaq>
        <Introduction>
          <IntroductionTitle id="webhooks" onClick={handleClickSection}>
            <TitleIcon>
              <IconWebhooks />
              <Heading variant="h5" color="primary_alt_medium">
                Webhooks
              </Heading>
            </TitleIcon>
            <ContainerArrow>{isOpen.webhooks ? <IconArrowUp /> : <IconArrowDown />}</ContainerArrow>
          </IntroductionTitle>
          <Collapsible open={isOpen.webhooks}>
            <IntroductionContent>
              <BodyText size="medium" weight="regular" color="gray-dark">
                Os webhooks permitem o envio assíncrono de notificações ou dados para sistemas externos sempre que ações
                específicas ocorrerem no seu produto. Ao criar um webhook, você define um caminho personalizado para as
                requisições externas e indica campos essenciais, como o id da conta, ou, de forma opcional, o campo
                associado ao id de um evento, ou falhas que possam estar relacionadas à requisição.
              </BodyText>
              <ListTypeDisc
                title="Content-Type:"
                description="Indique o formato dos dados retornados pelo webhook (application/json ou multipart/form-data)."
                className="disc"
                classNameLI="list-item"
              />
              <ListTypeDisc
                title="Persistent Fields:"
                description="Defina os campos retornados na resposta da requisição ao webhook, 
                que serão persistidos no banco de dados para consultas e uso posterior, caso exista um evento ou arquivo relacionados à requisição."
                className="disc"
                classNameLI="list-item"
              />
            </IntroductionContent>
          </Collapsible>
        </Introduction>
      </ContainerFaq>
    </ContainerList>
  )
}

export default memo(ListContent)
